import React, {Fragment} from 'react';
import { find, remove } from 'lodash';
import useSiteMetadata from '../hooks/use-site-metadata';
import { toLdJson } from './ldsjon';
import { getSrc } from 'gatsby-plugin-image';
import {Script} from 'gatsby';
import PropTypes from 'prop-types';

const generalForbiddenMetas = [
  'robots',
  'viewport',
  'og:title',
  'og:type',
  'og:image',
  'og:image:alt',
  'og:audio',
  'og:video',
  'og:description',
  'og:locale',
  'og:locale:alternate',
  'twitter:card',
];


const websiteForbiddenMetas = [
  'viewport',
  'og:title',
  'og:type',
  'og:image',
  'og:image:alt',
  'og:audio',
  'og:video',
  'og:description',
  'og:locale',
  'og:locale:alternate',
  'twitter:card',
];


const pageForbiddenMetas = [
  'viewport',
  'og:site_name',
  'twitter:card',
  'twitter:site',
  'twitter:creator',
];


const getWebsiteRoot = (siteMetadata, location) => {
  const {siteUrl, siteBaseUrl, sitePathPrefix} = siteMetadata;
  let siteRoot = siteUrl;
  if (siteBaseUrl
    && sitePathPrefix
    && location?.pathname?.startsWith(sitePathPrefix)) {
    siteRoot = siteBaseUrl;
  }
  return siteRoot;

};

/*
const getPageCannonicalLink = (siteMetadata, location) => {
  let siteRoot = getWebsiteRoot(siteMetadata, location)
  if (siteRoot && location && location.pathname) {
    let srarr = siteRoot.split('/')

    if(location.pathname.startsWith(`/${srarr[srarr.length -1]}`)){
      srarr.pop()
      siteRoot = srarr.join('/')
    }
    return siteRoot + location.pathname
  } else {
    return null
  }
}
*/


const getCoverMediaUrl = (siteMetadata, location, coverMedia) => {
  let src = null;
  if (coverMedia) {
    if (coverMedia.image) {
      src = getSrc(coverMedia.image);
    }
    if (coverMedia.imageFile) {
      src = getSrc(coverMedia.imageFile);
    }
  }

  if (!src) {
    return siteMetadata.defaultLogoUrl;
  }

  let siteUrl = getWebsiteRoot(siteMetadata, location);
  if (src && siteUrl) {
    const siteUrlArr = siteUrl.split('/');
    if (siteUrlArr[siteUrlArr.length -1] === '') {
      siteUrlArr.pop();
    }
    if (src.startsWith(`/${siteUrlArr[siteUrlArr.length -1]}`)){
      siteUrlArr.pop();
    }
    siteUrl = siteUrlArr.join('/');
    if (siteUrl.endsWith('/')) {
      return `${siteUrl.splice(0, -1)} + ${src}`;
    }
    return `${siteUrl}${src}`;

  }
  return null;
};


const deleteEntries = (toSearch, toRemove) => {
  if (toSearch && toRemove) {
    remove(toSearch, (meta) =>
      find(toRemove, (forbiddenMeta) => {
        return (meta.name && forbiddenMeta === meta.name)
          || (meta.property && forbiddenMeta === meta.property);
      })
    );
    return toSearch;
  }
  return [];
};

const replaceEntry = (toSearch, entry, replacement) => {
  if (toSearch && entry && replacement) {
    remove(toSearch, { name: entry });
    toSearch.push({ name: entry, content: replacement });
  }
  return toSearch;
};

const parseMetaDescription = (metaDescription) => {
  return metaDescription;
};

const getFaviconUrl = (favicon) => {
  if (favicon?.imageFile?.publicURL) {
    return favicon.imageFile.publicURL;
  }

  return '/favicon.svg';
};

const getOGSiteName = (website) => {
  if(website === '_ACCUEIL_SCIENCES_PO_') {
    return 'Sciences Po';
  }
  return website;
};

const toType = (type) => {
  const mp = {
    'Event': 'event',
    'News': 'article'
  };
  if (type in mp) {
    return mp[type];
  }
  return 'website';
};

const getPageTitle = (pageSeo, locale) => {
  if (pageSeo?.seo?.Meta) {
    for(const m of pageSeo.seo.Meta) {
      if (m.name === 'title' || m.title === 'title') {
        if(m.description) return m.description;
        if(m.content) return m.content;
      }
    }
  }
  if(pageSeo?.title) return pageSeo.title;
  return (locale === 'fr') ? 'Page inconnue' : 'Unknown Page';
};


const Seo = ({
  generalSeo,
  canonicalLink,
  pageSeo,
  websiteSeo,
  metaDescription,
  coverMedia,
  website,
  type,
  eventLocation,
  start_date,
  end_date,
  publishedAt,
  modification_date,
  eventLink,
  language,
  location,
  favicon,
  isScPoWebsite
}) => {


  const siteMetadata = useSiteMetadata();
  const {siteUrl} = siteMetadata;
  const locale = websiteSeo?.locale || pageSeo?.locale || 'fr';
  const title = getPageTitle(pageSeo, locale);

  const coverImageUrl = getCoverMediaUrl(siteMetadata, location, coverMedia);
  const faviconUrl = getFaviconUrl(favicon);

  const generalMetas = deleteEntries(generalSeo?.Meta, generalForbiddenMetas);
  const websiteMetas = deleteEntries(websiteSeo?.seo?.Meta, websiteForbiddenMetas);
  let pageMetas = deleteEntries(pageSeo?.seo?.Meta, pageForbiddenMetas);


  const description =
    parseMetaDescription(metaDescription) ||
    find(pageMetas, { name: 'description' })?.content ||
    find(websiteMetas, { name: 'description' })?.content ||
    websiteSeo?.description ||
    find(generalMetas, { name: 'description' })?.content ||
    null;
  pageMetas = replaceEntry(pageMetas, 'description', description);

  const defaultMetas = [
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: toType(type) },
    { property: 'og:image', content: coverImageUrl },
    {
      property: 'og:image:alt',
      content:
        locale === 'fr'
          ? coverMedia?.alternativeText
          : coverMedia?.alternativeTextEN || '',
    },
    { property: 'og:locale', content: locale },
    { property: 'og:locale:alternate', content: locale === 'fr' ? 'en' : 'fr' },
    { property: 'og:site_name', content: getOGSiteName(website) },
    { name: 'twitter:title', content: title },
    { name: 'twitter:image', content: coverImageUrl },
    { name: 'twitter:description', content: description },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: getOGSiteName(website) },
    { name: 'twitter:creator', content: '@sciencespo' },
  ];

  if (type === 'Event') {
    if(start_date) {
      defaultMetas.push({property: 'og:event:start_time', content: start_date});
    }
    if(start_date) {
      defaultMetas.push({property: 'og:event:end_time', content: end_date});
    }
  }

  const getCanonicalLink = (canonicalLink, pageMetas) => {
    if (pageMetas) {
      for(const meta of pageMetas) {
        if (meta && meta.name === 'canonical') {
          return meta.description;
        }
      }
    }
    return canonicalLink;
  };

  const getWebsiteName = () => {
    if (websiteSeo?.name
        && !['_ACCUEIL_SCIENCES_PO_'].includes(websiteSeo.name)) {
      return websiteSeo.name;
    }
    return '';
  };

  const metas = [];
  const metaSet = {};
  const allMetas = [...pageMetas, ...websiteMetas, ...generalMetas, ...defaultMetas].filter(e => !!e);

  const checkCanonical = (link) => {
    if (link && !link.endsWith('/')) {
      return `${link}/`;
    }
    return link;
  };

  let canonical = getCanonicalLink (checkCanonical(canonicalLink), pageMetas);
  allMetas.forEach(meta => {
    const prop = meta.name || meta.property;
    if (meta.content && meta.content !=='') {
      if (prop === 'script' || prop === 'link') {
        metas.push(meta);
      } else {
        if (!(prop in metaSet)) {
          metaSet[prop] = 1;
          if (prop === 'canonical') {
            canonical = meta.content;
          } else {
            metas.push(meta);
          }
        }
      }
    }
  });

  const ldJson = toLdJson({
    type, title, description,
    publishedAt,
    modification_date,
    canonical,
    language,
    siteUrl,
    eventLink,
    coverImageUrl,
    start_date, end_date, eventLocation
  });

  const toScript = (scr, index) => {
    if (scr.startsWith('http://') || scr.startsWith('//') || scr.startsWith('https://')) {
      return (
        <Script key={'script-' + index}
          src={scr}
          strategy="idle"
        />
      );
    }
    return (
      <Script key={'script-' + index}
        strategy="idle"
      >
        {scr}
      </Script>
    );
  };

  const getInstitutionName = () => {
    if (isScPoWebsite) {
      return ' Sciences Po';
    }
    return '';
  };

  return (
    <>
      <html lang={(locale || 'fr').toLowerCase()}/>
      <title key={'title'}>{`${title} |${getInstitutionName()} ${getWebsiteName()}`}</title>
      {canonical && (
        <link href={canonical}
          rel="canonical"
        />
      )}
      <link href={faviconUrl}
        rel="icon"
        type="image/svg+xml"
      />
      <link href={faviconUrl}
        rel="shortcut icon"
      />
      {metas.map((meta, index)=> {
        if (meta.name && meta.name !== 'script' && meta.name !== 'link') {
          return (
            <meta content={meta.content}
              key={index}
              name={meta.name}
            />
          );
        }
        if (meta.property) {
          return (
            <meta content={meta.content}
              key={index}
              property={meta.property}
            />
          );
        }
        return (<Fragment key={'frgm-'+index}></Fragment>);
      })
      }
      {ldJson && (
        <Script type="application/ld+json">{ldJson}</Script>
      )}
      {
        metas.map((meta, index) => {
          if (meta.name && meta.name === 'script') {
            return toScript(meta.content, index);
          }
          if (meta.name && meta.name === 'link') {
            return (
              <link
                href={meta.content}
                hrefLang={meta.language}
                key={`link-${index}`}
                rel="alternate"
              >
              </link>);
          }
          //return (<Fragment key={'frg-script-' + index}></Fragment>);
        })
      }
    </>
  );
};

Seo.propTypes = {
  canonicalLink: PropTypes.string,
  coverMedia: PropTypes.object,
  end_date: PropTypes.string,
  eventLink: PropTypes.string,
  eventLocation: PropTypes.string,
  favicon: PropTypes.object,
  generalSeo: PropTypes.object,
  isScPoWebsite: PropTypes.bool,
  language: PropTypes.string,
  location: PropTypes.object,
  metaDescription: PropTypes.string,
  modification_date: PropTypes.string,
  pageSeo: PropTypes.object,
  publishedAt: PropTypes.string,
  start_date: PropTypes.string,
  type: PropTypes.string,
  website: PropTypes.string,
  websiteSeo: PropTypes.object,
};

export default Seo;
